import * as React from 'react'
import { WithTranslation, withTranslation } from "react-i18next";
import { Contact } from "../../../types";
import { ContactsController } from "../../../controllers";
import SimplebarHeader from "../../Simplebar/SimplebarHeader";
import SimplebarContainer from "../../Simplebar/SimplebarContainer";
import SimplebarContent from "../../Simplebar/SimplebarContent";
import SimplebarDescription from "../../Simplebar/SimplebarDescription";
import SimplebarItems from '../../Simplebar/SimplebarItems';
import SimplebarItem from '../../Simplebar/SimplebarItem';
import ContactTypeAvatar from '../../Avatar/ContactTypeAvatar';
import SimplebarLoader from '../../Simplebar/SimplebarLoader';

type IProps = {
  active: boolean
  onItemClick: (contact: Contact) => void
} & WithTranslation

interface IState {
  starredContacts: Contact[],
  currentPage: number,
  totalPages: number
  didInitialLoad: boolean
  reachedEnd: boolean
  isFetching: boolean
}

class StarredContactsSimplebar extends React.Component<IProps, IState> {
  private container = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)

    this.state = {
      starredContacts: [],
      currentPage: 0,
      totalPages: 0,
      didInitialLoad: false,
      isFetching: false,
      reachedEnd: false
    }

    this.fetchStarredContacts = this.fetchStarredContacts.bind(this)
    this.onScrollChange = this.onScrollChange.bind(this)
    this.onItemClick = this.onItemClick.bind(this)
  }

  componentDidMount() {
    this.fetchStarredContacts(1)
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (this.props.active && !prevProps.active) {
      this.container.current.scrollTop = 0
      this.fetchStarredContacts(1)
    }
  }

  async fetchStarredContacts(page: number) {
    const { starredContacts: stateContacts } = this.state
    this.setState({
      isFetching: true
    }, async () => {
      try {
        const response = await ContactsController.getContacts({
          page: page,
          starred: true,
          order: 'created_at_desc',
        })
        const { contacts, current_page, total_pages, total_entries } = response;

        this.setState({
          starredContacts: current_page === 1 ? [...contacts] : [...stateContacts, ...contacts],
          currentPage: current_page,
          totalPages: total_pages,
          didInitialLoad: true,
          isFetching: false,
          reachedEnd: current_page === total_pages
        });
      } catch (ex) {
        console.error(ex)
      }
    });
  }

  onScrollChange(event) {
    const { currentPage, reachedEnd, isFetching } = this.state
    const node = event.target;
    const scrollListEndReached = node.scrollHeight - node.scrollTop <= node.clientHeight;

    if (scrollListEndReached && !reachedEnd && !isFetching) {
      this.fetchStarredContacts(currentPage + 1)
    }
  }

  onItemClick(contact: Contact) {
    this.props.onItemClick(contact)
  }

  render() {
    const { t } = this.props
    const { starredContacts, didInitialLoad } = this.state

    return (
      <SimplebarContainer>
        <SimplebarHeader>
          {t('StarredContactsSimplebar::Starred contacts')}
        </SimplebarHeader>

        <SimplebarContent ref={this.container} onScroll={this.onScrollChange}>
          {!didInitialLoad && <SimplebarLoader />}
          {didInitialLoad && <>
            {starredContacts?.length === 0 && <SimplebarDescription>
              {t('StarredContactsSimplebar::When you star a contact from their profile, they will show up here.')}
            </SimplebarDescription>}
            {starredContacts?.length > 0 && <SimplebarItems>
              {starredContacts.map((contact, index) => {
                return (
                  <SimplebarItem key={contact.id} onClick={() => this.onItemClick(contact)} title={contact.name}>
                    <ContactTypeAvatar width={30} contact={contact} />
                    <span>{contact.name}</span>
                  </SimplebarItem>
                )
              })}
            </SimplebarItems>}
          </>}
        </SimplebarContent>
      </SimplebarContainer>
    )
  }
}

export default withTranslation()(StarredContactsSimplebar)